import { ActionableContainerWithTitle } from '~/components/Cards';
import {
  generateRedirectionToApplicationListURL,
  getTranslationKey,
  handleDeleteApplication
} from './utils';
import { Localized } from '~/i18n';
import ApplicationDetailsForm from './ApplicationDetailsForm';
import { ApiConfigData, EApplicationType } from '~/api';
import ClientSecrets from '../Settings/APICredentials/ClientSecrets';
import { Button } from '~/components/ui/Button';
import { useIntl } from '@cookbook/solid-intl';
import { useToast } from '~/components/ui/Toast';
import { useConfirm } from '~/components/ui/Modal';
import { useQueryClient } from '@tanstack/solid-query';
import { mergeProps, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { useAppData } from '~/components/AppData';
import { ClientSecretFlowEnum } from '../Settings/utils';
import { ark } from '@ark-ui/solid';
import { getLocalizedString } from '~/i18n/utils';
import { ReadOnlyField } from '~/components/ui/ReadOnlyField';

export type ConfigureMultiAppProps = {
  applicationData: ApiConfigData;
};

export default function ConfigureMultiApp(props: ConfigureMultiAppProps) {
  const merged = mergeProps({}, props);
  const intl = useIntl();
  const toaster = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const appData = useAppData();

  return (
    <div>
      <ActionableContainerWithTitle class={`mt-0 relative min-w-[880px]`}>
        <ApplicationDetailsForm applicationData={merged.applicationData} />
      </ActionableContainerWithTitle>

      <Show
        when={
          merged.applicationData.clientType ===
            EApplicationType.DEFAULT_APPLICATION ||
          merged.applicationData.clientType === EApplicationType.WEB_APPLICATION
        }
      >
        <ActionableContainerWithTitle
          header={getLocalizedString(
            getTranslationKey('client_credential_block_header')
          )}
          subHeader={
            <div class="mb-6">
              <Localized
                translationKey={getTranslationKey(
                  'client_credential_block_sub_header'
                )}
              />
            </div>
          }
        >
          <div class="mb-6 max-w-[700px]">
            <ark.label class={`text-fg-default mb-1 font-medium text-sm`}>
              {getLocalizedString(
                getTranslationKey(`details_header_client_id`)
              )}
            </ark.label>
            <p class={`text-sm mb-1.5 text-fg-subtle`}>
              {getLocalizedString(getTranslationKey(`client_id_subtext`))}
            </p>
            <ReadOnlyField
              value={merged.applicationData.keyId}
              width={'full'}
            />
          </div>
          <ClientSecrets
            clientCredentials={merged.applicationData}
            flowType={ClientSecretFlowEnum.MULTI_APPLICATION}
            class="pl-0 pb-0"
          />
        </ActionableContainerWithTitle>
      </Show>

      <Show
        when={
          merged.applicationData.clientType !==
          EApplicationType.DEFAULT_APPLICATION
        }
      >
        <ActionableContainerWithTitle
          header={
            <div class="flex justify-between">
              <span>
                <Localized
                  translationKey={getTranslationKey(
                    'configure_delete_block_header'
                  )}
                />
              </span>
            </div>
          }
          subHeader={getLocalizedString(
            getTranslationKey('configure_delete_block_sub_header')
          )}
          ctaLabel={
            <Button
              variant="outline"
              class={`hover:bg-bg-default`}
              color="danger"
              onClick={async () => {
                const deleteSuccess = await handleDeleteApplication({
                  data: merged.applicationData,
                  confirm,
                  intl,
                  queryClient,
                  toaster
                });

                if (deleteSuccess) {
                  navigate(
                    generateRedirectionToApplicationListURL(
                      appData.selectedEnvironment?.id!
                    )
                  );
                }
              }}
              data-testid={`multi-app-delete-button`}
            >
              <Localized translationKey={getTranslationKey('cta_delete')} />
            </Button>
          }
          class="bg-bg-muted"
        >
          <></>
        </ActionableContainerWithTitle>
      </Show>
    </div>
  );
}
